import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import InitialQuestionnaire from '../InitialQuestionnaire';
import SingleQuestion from '../SingleQuestion';
import { useAppContext } from '../../contexts/AppContext';
import styles from './index.module.scss';
import { sendMessageToDify } from '../../api/request';
import LoadingAnimation from '../LoadingAnimation';

const WELCOME_MESSAGE = {
  type: 'ai',
  content: '您好，我是北航器件制备工艺智能助手。请简单描述您的工艺流程需求，我将为您生成相应的思维导图。'
};

const QUESTIONNAIRE_PROMPT_MESSAGE_ID = 'ai_initial_questionnaire_prompt';

const QUESTIONNAIRE_PROMPT_MESSAGE = {
  type: 'ai',
  id: QUESTIONNAIRE_PROMPT_MESSAGE_ID,
  content: '请完成以下问题',
  meta: {
    kind: 'initial_questionnaire_prompt'
  }
};

// 添加提取特殊文本块的函数
const extractSpecialBlocks = (text) => {
  const input = String(text || '');
  const blocks = [];

  // 仅识别“换行后的单@”作为封装标志：(^|\n)@\n ... (^|\n)@(?=\n|$)
  // 关键点：
  // - 必须是“单独一行的 @”，前后紧邻换行
  // - 允许出现在文本开头或结尾（不要求一定有 \n@\n）
  // - 文本内部可能出现其它 @（例如邮箱/标记），不能误判
  const re = /(^|\n)@\n([\s\S]*?)\n@(?=\n|$)/g;
  let m;
  while ((m = re.exec(input)) !== null) {
    const content = m[2];
    const titleMatch = content.match(/^\s*#\s+(.*?)(?:\n|$)/m);
    const fileName = titleMatch ? `${titleMatch[1].trim()}.md` : `document_${blocks.length + 1}.md`;
    blocks.push({
      fileName,
      content,
      fullMatch: m[0]
    });
  }

  return blocks;
};

const stripSpecialBlocks = (text) => {
  const input = String(text || '');
  return input.replace(/(^|\n)@\n[\s\S]*?\n@(?=\n|$)/g, '\n');
};

// 单题表格：支持前缀文本 + 表格在同一行内
// 目标：
// - prefix: 题目前方文本（示例："清洗"）保留在气泡中，并传给弹窗显示“对于{prefix}流程的问题”
// - question: Qn + 题干
// - options: 只取表格中的选项列（示例：提升附着力 / 构建功能层 / 不确定）
// - 忽略跳转块："**A:** → Q2 <br> **B:** → 方法 6 ..."（不展示、不作为选项）
const extractSingleQuestionFromText = (text) => {
  const raw = String(text || '');
  // AI 有时会把换行变成 <br>，先统一为 \n，方便做切割
  const normalized = raw.replace(/<br\s*\/?\s*>/gi, '\n');

  const qIndex = normalized.search(/\|\s*\*{0,2}Q\d+\*{0,2}\s*\|/i);
  if (qIndex < 0) return null;

  // prefix 是 Q 表格出现前的文本（去掉尾部多余空白）
  const prefix = normalized.slice(0, qIndex).trim();

  // 从表格起始管道开始截取，直到出现跳转块（A/B/C: → ...）或换行结束
  let tablePart = normalized.slice(qIndex);

  const jumpIndex = tablePart.search(/\*{0,2}[A-Z]\*{0,2}\s*[:：]\s*→/);
  if (jumpIndex >= 0) {
    tablePart = tablePart.slice(0, jumpIndex);
  }

  // 只取表格第一行的内容（如果后面还有其他文本，不算进来）
  tablePart = tablePart.split('\n')[0];

  // 拆单元格：| Q1 | 题干 | 选项1 | 选项2 | ... |
  const cells = tablePart
    .trim()
    .replace(/^.*?\|/, '|')
    .replace(/^\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim())
    .filter(Boolean);

  if (cells.length < 3) return null;

  const qCell = String(cells[0] || '').replace(/\*/g, '').trim();
  const qMatch = qCell.match(/Q\s*(\d+)/i);
  const qid = qMatch ? `Q${qMatch[1]}` : qCell;

  const title = String(cells[1] || '').replace(/\*\*/g, '').trim();

  const optionCells = cells
    .slice(2)
    .map((c) => String(c).replace(/\*\*/g, '').trim())
    .filter((c) => c && !/^[A-Z]\s*[:：]/.test(c));

  if (optionCells.length === 0) return null;

  const options = optionCells.map((opt, idx) => ({
    letter: String.fromCharCode('A'.charCodeAt(0) + idx),
    text: opt
  }));

  // 用于从消息气泡里剔除题目部分：从 qIndex 开始到跳转块前（或到该行结束）
  const fullMatch = normalized.slice(qIndex, qIndex + tablePart.length);

  return {
    id: qid,
    title,
    options,
    prefix,
    fullMatch
  };
};

const isAutoContinueText = (text) => {
  const input = String(text || '').trim();
  // 类似：匀胶：方法 7 / 去底胶：方法五（中文冒号或英文冒号；中文数字或阿拉伯数字）
  if (!input) return false;
  if (/```mermaid[\s\S]*```/m.test(input)) return false;
  if (/\|\s*\*?\*?Q\d+/i.test(input)) return false;
  if (/\n@\n[\s\S]*\n@\n/m.test(input)) return false;

  // 支持：方法7 / 方法 7 / 方法七 / 方法 七 / 方法五 等
  // 同时支持单个方法和多个方法的情况，如：
  // "清洗：方法 11"
  // "清洗：方法 11，方法12"
  // "清洗：方法 11、方法12"
  // "清洗：方法 11,方法12"
  const numLike = '(?:\\d+|[一二三四五六七八九十百零两]+)';
  // 单个方法：方法[空格可选]数字
  const singleMethod = `方法\\s*${numLike}`;
  // 多个方法：用逗号、顿号或空格分隔
  const multipleMethods = `${singleMethod}(?:\\s*[，,、]\\s*${singleMethod})*`;
  // 完整匹配：开头0-40个字符，然后冒号，然后是单个或多个方法
  const re = new RegExp(`^.{0,40}[：:]\\s*${multipleMethods}\\s*$`, 'm');
  return re.test(input);
};

// 添加下载功能
const downloadMarkdown = (fileName, content) => {
  console.log('[ChatDialog][downloadMarkdown]', { fileName, len: String(content || '').length });
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ChatDialog = forwardRef(({ visible, onClose, onMermaidData, onProcessesDetected, autoOpenInitialQuestionnaire = false, forceOpenInitialQuestionnaireSignal = 0 }, ref) => {
  // 从Context获取聊天状态
  const {
    chatMessages,
    setChatMessages,
    conversationId,
    setConversationId
  } = useAppContext();

  const [inputValue, setInputValue] = useState('');
  const [initialDeviceName, setInitialDeviceName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuestionnaireDetail, setShowQuestionnaireDetail] = useState(false);
  const [currentQuestionnaireDetail, setCurrentQuestionnaireDetail] = useState(null);
  const [userId] = useState(`test`);
  const messagesEndRef = useRef(null);
  const dialogRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [showInitialQuestionnaire, setShowInitialQuestionnaire] = useState(false);
  const [initialQuestionnaireCompleted, setInitialQuestionnaireCompleted] = useState(false);
  const [initialQuestionnaireAnswers, setInitialQuestionnaireAnswers] = useState({});
  const [initialQuestionnaireAnswerText, setInitialQuestionnaireAnswerText] = useState('');
  const [questionnaireAttachmentVisible, setQuestionnaireAttachmentVisible] = useState(false);
  const [hasSentInitialQuestionnaire, setHasSentInitialQuestionnaire] = useState(false);
  const [initialQuestionnaireLocked, setInitialQuestionnaireLocked] = useState(false);

  // AI 单题问答流程
  const [singleQuestionVisible, setSingleQuestionVisible] = useState(false);
  const [currentSingleQuestion, setCurrentSingleQuestion] = useState(null);
  const [hasPendingSingleQuestion, setHasPendingSingleQuestion] = useState(false);
  const [flowEnded, setFlowEnded] = useState(false);

  // 单题等待态：用户已提交当前题，等待 AI 返回下一题
  const [singleQuestionWaiting, setSingleQuestionWaiting] = useState(false);

  // 单题历史记录（像聊天记录一样保留在表单中）
  // 每条记录在用户确认选择后写入，并在后续随时可查看（只读）。
  const [singleQuestionHistory, setSingleQuestionHistory] = useState([]);

  // 使用 ref 保证发送时拿到最新 conversationId（避免闭包导致一直是空）
  const conversationIdRef = useRef(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // 处理查看问卷详情
  const handleViewQuestionnaireDetail = (submissionData) => {
    // 停止任何正在进行的拖拽操作
    setIsDragging(false);
    setCurrentQuestionnaireDetail(submissionData);
    setShowQuestionnaireDetail(true);
  };

  // 关闭问卷详情弹窗
  const closeQuestionnaireDetail = useCallback(() => {
    setShowQuestionnaireDetail(false);
    setCurrentQuestionnaireDetail(null);
  }, []);

  // ESC键关闭弹窗和防止背景滚动
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showQuestionnaireDetail) {
        closeQuestionnaireDetail();
      }
    };

    if (showQuestionnaireDetail) {
      document.addEventListener('keydown', handleKeyDown);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showQuestionnaireDetail, closeQuestionnaireDetail]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // 初始化时设置欢迎消息
  useEffect(() => {
    if (chatMessages.length === 0) {
      setChatMessages([WELCOME_MESSAGE, QUESTIONNAIRE_PROMPT_MESSAGE]);
    }
  }, [chatMessages.length, setChatMessages]);

  // 滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // 提取 Mermaid 数据
  const extractMermaidData = (text) => {
    const mermaidMatch = text.match(/```mermaid\s*([\s\S]*?)\s*```/);
    return mermaidMatch ? mermaidMatch[1].trim() : null;
  };

  const stripMermaidBlocks = (text) => {
    return String(text || '').replace(/```mermaid[\s\S]*?```/g, '');
  };


  // 提取工艺流程数组
  const extractProcessArray = (text) => {
    const textWithoutMermaid = text.replace(/```mermaid[\s\S]*?```/g, '');
    const arrayMatch = textWithoutMermaid.match(/\[(.*?)\]/);
    if (arrayMatch) {
      try {
        const arrayStr = arrayMatch[0].replace(/'/g, '"');
        const processArray = JSON.parse(arrayStr);
        if (Array.isArray(processArray) && processArray.length > 0) {
          return processArray;
        }
      } catch (e) {
        console.error('解析工艺流程数组失败:', e);
      }
    }
    return null;
  };

  const sendAutoQuery = async (query, meta = {}) => {
    if (isLoading) return;
    if (!initialQuestionnaireCompleted) return;

    const queryText = String(query);
    const showUserBubble = Boolean(meta?.showUserBubble);


    // 仅隐藏自动续聊 query=1；用户选择 A/B/C 需要展示用户气泡
    if (showUserBubble) {
      const userMessage = {
        type: 'user',
        content: queryText,
        timestamp: Date.now(),
        id: `user_${Date.now()}`
      };
      setChatMessages(prev => [...prev, userMessage, {
        type: 'thinking',
        timestamp: Date.now() + 1,
        id: `thinking_${Date.now()}`
      }]);
    } else {
      setChatMessages(prev => [...prev, {
        type: 'thinking',
        timestamp: Date.now(),
        id: `thinking_${Date.now()}`
      }]);
    }

    setIsLoading(true);

    try {
      const response = await sendMessageToDify(queryText, userId, conversationIdRef.current);

      if (response.conversation_id) {
        setConversationId(response.conversation_id);
        conversationIdRef.current = response.conversation_id;
      }

      const answer = response.answer || '抱歉，我现在无法回答这个问题。';

      await handleAiAnswer(answer);
    } catch (error) {
      console.error('自动发送消息失败:', error);
      setChatMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        const errorMessage = {
          type: 'ai',
          content: '抱歉，发生了一些错误，请稍后重试。',
          timestamp: Date.now(),
          id: `error_${Date.now()}`
        };

        if (lastMessage && lastMessage.type === 'thinking') {
          return prev.slice(0, -1).concat([errorMessage]);
        }
        return [...prev, errorMessage];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAiAnswer = async (answer) => {

    console.groupCollapsed('[ChatDialog][handleAiAnswer]');
    console.log('[ChatDialog][handleAiAnswer] raw answer length:', String(answer || '').length);

    const processArray = extractProcessArray(answer);
    if (processArray) {
      console.log('[ChatDialog][handleAiAnswer] processArray detected:', processArray);
      onProcessesDetected?.(processArray);
    }

    const mermaidData = extractMermaidData(answer);
    if (mermaidData) {
      console.log('[ChatDialog][handleAiAnswer] mermaid detected, length:', mermaidData.length);
      onMermaidData?.(mermaidData);
    }

    const answerWithoutMermaid = stripMermaidBlocks(answer);
    console.log('[ChatDialog][handleAiAnswer] answerWithoutMermaid length:', answerWithoutMermaid.length);

    const mdBlocks = extractSpecialBlocks(answerWithoutMermaid);
    console.log('[ChatDialog][handleAiAnswer] mdBlocks detected:', mdBlocks.map(b => ({ fileName: b.fileName, len: b.content?.length })));

    const singleQ = extractSingleQuestionFromText(answerWithoutMermaid);
    console.log('[ChatDialog][handleAiAnswer] singleQ detected:', singleQ ? { id: singleQ.id, title: singleQ.title, prefix: singleQ.prefix, optionCount: singleQ.options?.length } : null);

    const shouldAutoContinue = isAutoContinueText(answerWithoutMermaid);
    console.log('[ChatDialog][handleAiAnswer] shouldAutoContinue:', shouldAutoContinue);


    if (mdBlocks.length > 0) {
      console.log('[ChatDialog][handleAiAnswer] flow end detected by mdBlocks');
      setFlowEnded(true);
      // 结束时：单题表单不应继续转圈等待
      setSingleQuestionWaiting(false);
      setHasPendingSingleQuestion(false);
      setCurrentSingleQuestion(null);
    }

    if (singleQ) {
      setCurrentSingleQuestion({
        id: singleQ.id,
        title: singleQ.title,
        options: singleQ.options,
        prefix: singleQ.prefix
      });
      setHasPendingSingleQuestion(true);
      setSingleQuestionVisible(true);
      setSingleQuestionWaiting(false);
    }

    let displayText = String(answer || '');
    displayText = displayText
      .replace(/```mermaid\s*([\s\S]*?)\s*```/g, () => '【mermaid流程图数据-点击复制-点击渲染】')
      .replace(/```/g, '');

    // ===== 单题气泡策略（彻底避免残留） =====
    // 只要命中 singleQ：
    // - 气泡只保留 prefix（例如“清洗/去底胶”）
    // - 表格、选项、跳转块全部不进入气泡内容
    // 这样不会再出现 **B:** → 方法 6 / **C:** → ... 之类残留。
    if (singleQ) {
      displayText = String(singleQ.prefix || '').trim();
    } else {
      displayText = stripSpecialBlocks(displayText);
    }

    if (shouldAutoContinue) {

      const aiMessage = {
        type: 'ai',
        content: displayText,
        mermaidData: mermaidData,
        mdBlocks,
        singleQuestion: singleQ ? { id: singleQ.id, prefix: singleQ.prefix } : null
      };

      setChatMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        const packed = { ...aiMessage, timestamp: Date.now(), id: `ai_${Date.now()}` };
        if (lastMessage && lastMessage.type === 'thinking') {
          return prev.slice(0, -1).concat([packed]);
        }
        return [...prev, packed];
      });

      setIsLoading(false);
      await sendAutoQuery('1', { reason: 'auto_continue_text' });
      return;
    }

    const aiMessage = {
      type: 'ai',
      content: displayText,
      mermaidData: mermaidData,
      mdBlocks,
      singleQuestion: singleQ ? { id: singleQ.id, prefix: singleQ.prefix } : null
    };

    console.log('[ChatDialog][handleAiAnswer] final displayText length:', String(displayText || '').length);
    console.log('[ChatDialog][handleAiAnswer] final displayText head:', String(displayText || '').slice(0, 200));

    setChatMessages(prev => {
      const lastMessage = prev[prev.length - 1];
      const packed = {
        ...aiMessage,
        timestamp: Date.now(),
        id: `ai_${Date.now()}`
      };

      console.log('[ChatDialog][handleAiAnswer] push ai message:', {
        id: packed.id,
        hasMermaid: Boolean(packed.mermaidData),
        mdBlockCount: packed.mdBlocks?.length || 0,
        hasSingleQuestion: Boolean(packed.singleQuestion),
        contentPreview: String(packed.content || '').slice(0, 120)
      });

      console.groupEnd();

      if (lastMessage && lastMessage.type === 'thinking') {
        return prev.slice(0, -1).concat([packed]);
      }
      return [...prev, packed];
    });
  };

  // 处理复制和渲染
  const handleCopyAndRender = async (mermaidData) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = mermaidData;
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!success) {
        throw new Error('复制失败');
      }

      onMermaidData?.(mermaidData + '\n', false);

      setTimeout(() => {
        onMermaidData?.(mermaidData, true);
      }, 100);

      alert('流程图已重新渲染，数据已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  // 处理用户输入发送
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    // 初始表单完成前：禁止从聊天输入框触发首句（输入内容已迁移到初始表单第一步）
    if (!initialQuestionnaireCompleted) {
      return;
    }

    const isFirstMessage = !hasSentInitialQuestionnaire;
    const questionnaireBlock = `问题答案：\n${initialQuestionnaireAnswerText}`;
    const queryTextWithQuestionnaire = `制备仪器名称：\n${inputValue}\n\n${questionnaireBlock}`;
    const queryText = hasSentInitialQuestionnaire ? inputValue : queryTextWithQuestionnaire;
    

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: Date.now(),
      id: `user_${Date.now()}`
    };

    setChatMessages(prev => [...prev, userMessage, {
      type: 'thinking',
      timestamp: Date.now() + 1,
      id: `thinking_${Date.now()}`
    }]);
    setInputValue('');
    setIsLoading(true);
    if (!hasSentInitialQuestionnaire) {
      setQuestionnaireAttachmentVisible(true);
    }

    try {
      const response = await sendMessageToDify(queryText, userId, conversationIdRef.current);
      if (!hasSentInitialQuestionnaire) {
        setHasSentInitialQuestionnaire(true);
        setQuestionnaireAttachmentVisible(false);
        setInitialQuestionnaireLocked(true);
      }

      if (response.conversation_id) {
        setConversationId(response.conversation_id);
        conversationIdRef.current = response.conversation_id;
      }

      const answer = response.answer || '抱歉，我现在无法回答这个问题。';

      await handleAiAnswer(answer);
    } catch (error) {
      console.error('发送消息失败:', error);
      setChatMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        const errorMessage = {
          type: 'ai',
          content: '抱歉，发生了一些错误，请稍后重试。',
          timestamp: Date.now(),
          id: `error_${Date.now()}`
        };

        if (lastMessage && lastMessage.type === 'thinking') {
          return prev.slice(0, -1).concat([errorMessage]);
        } else {
          return [...prev, errorMessage];
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!initialQuestionnaireCompleted) {
        alert('请完成问题表单');
        return;
      }
      handleSend();
    }
  };

  const handleOpenInitialQuestionnaire = () => {
    setShowInitialQuestionnaire(true);
  };

  // 可由外部控制：对话框首次打开时自动弹出初始问卷
  useEffect(() => {
    if (!visible) return;
    if (!autoOpenInitialQuestionnaire) return;
    if (initialQuestionnaireCompleted) return;
    setShowInitialQuestionnaire(true);
  }, [visible, autoOpenInitialQuestionnaire, initialQuestionnaireCompleted]);

  // 外部强制打开初始问卷（用于点击“请点击输入您想制备的器件”等显式初始化入口）
  useEffect(() => {
    if (!visible) return;
    if (initialQuestionnaireCompleted) return;
    if (!forceOpenInitialQuestionnaireSignal) return;
    setShowInitialQuestionnaire(true);
  }, [visible, initialQuestionnaireCompleted, forceOpenInitialQuestionnaireSignal]);


  const handleInitialQuestionnaireComplete = ({ answers, answerText }) => {
    setInitialQuestionnaireCompleted(true);
    setInitialQuestionnaireAnswers(answers || {});
    setInitialQuestionnaireAnswerText(answerText);
    setShowInitialQuestionnaire(false);
    setQuestionnaireAttachmentVisible(true);

    // 完成表单后：
    // 1) 把器件名称写回输入框（相当于用户在输入框输入的内容迁移到问卷第一步）
    // 2) 等待附件气泡/按钮状态更新后，自动点击一次发送（只影响首句）
    const device = String(initialDeviceName || '').trim();
    if (!device) return;

    setInputValue(device);

    const autoSend = () => {
      const btn = dialogRef.current?.querySelector(`.${styles.sendButton}`);
      if (btn && !btn.disabled) {
        btn.click();
        return true;
      }
      return false;
    };

    // 先尝试下一帧发送；不行则 0.5s 兜底
    setTimeout(() => {
      const ok = autoSend();
      if (ok) return;

      setTimeout(() => {
        autoSend();
      }, 500);
    }, 0);
  };

  // 检测是否为移动设备
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 重置位置
  useEffect(() => {
    if (!isMobile && visible) {
      setPosition(null);
    }
  }, [visible, isMobile]);

  // 拖动相关逻辑
  const handleMouseDown = (e) => {
    if (isMobile || showQuestionnaireDetail) return;

    const dialog = dialogRef.current;
    const rect = dialog.getBoundingClientRect();

    if (!position) {
      setPosition({
        x: rect.left,
        y: rect.top
      });
    }

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || isMobile) return;

    const dialog = dialogRef.current;
    const maxX = window.innerWidth - dialog.offsetWidth;
    const maxY = window.innerHeight - dialog.offsetHeight;

    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;

    newX = Math.max(0, Math.min(maxX, newX));
    newY = Math.max(0, Math.min(maxY, newY));

    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging && !showQuestionnaireDetail) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, showQuestionnaireDetail]);

  // 暴露给 Home 用：判断是否有待答问题、打开/关闭问题弹窗
  useImperativeHandle(ref, () => ({
    resetChat: () => {
      setChatMessages([WELCOME_MESSAGE, QUESTIONNAIRE_PROMPT_MESSAGE]);
      setConversationId('');
      setInputValue('');
      setInitialDeviceName('');
      setInitialQuestionnaireCompleted(false);
      setInitialQuestionnaireAnswers({});
      setInitialQuestionnaireAnswerText('');
      setQuestionnaireAttachmentVisible(false);
      setShowInitialQuestionnaire(false);
      setHasSentInitialQuestionnaire(false);
      setInitialQuestionnaireLocked(false);
      setFlowEnded(false);
      setHasPendingSingleQuestion(false);
      setSingleQuestionVisible(false);
      setCurrentSingleQuestion(null);
      setSingleQuestionHistory([]);
      setSingleQuestionWaiting(false);
    },
    isQuestionPending: () => Boolean(hasPendingSingleQuestion && currentSingleQuestion),
    toggleQuestionDialog: () => {
      if (!(hasPendingSingleQuestion && currentSingleQuestion)) return false;
      setSingleQuestionVisible(v => !v);
      return true;
    },
    sendMessage: async (message, showThinking = true) => {
      const mermaidData = extractMermaidData(message);

      const processedMessage = message.replace(/```mermaid\s*([\s\S]*?)\s*```/g,
        () => '【mermaid流程图数据-点击复制-点击渲染】')
        .replace(/```/g, '');

      const userMessage = {
        type: 'user',
        content: processedMessage,
        mermaidData: mermaidData,
        timestamp: Date.now(),
        id: `user_${Date.now()}`
      };

      if (showThinking) {
        setChatMessages(prev => [...prev, userMessage, {
          type: 'thinking',
          timestamp: Date.now() + 1,
          id: `thinking_${Date.now()}`
        }]);
      } else {
        setChatMessages(prev => [...prev, userMessage]);
      }

      try {
        const response = await sendMessageToDify(message, userId, conversationIdRef.current);

        if (response.conversation_id) {
          setConversationId(response.conversation_id);
          conversationIdRef.current = response.conversation_id;
        }

        const answer = response.answer || '抱歉，我现在无法回答这个问题。';

        await handleAiAnswer(answer);
      } catch (error) {
        console.error('发送消息失败:', error);
        setChatMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          const errorMessage = {
            type: 'ai',
            content: '抱歉，发生了一些错误，请稍后重试。',
            timestamp: Date.now(),
            id: `error_${Date.now()}`
          };

          if (lastMessage && lastMessage.type === 'thinking') {
            return prev.slice(0, -1).concat([errorMessage]);
          } else {
            return [...prev, errorMessage];
          }
        });
      }
    },
    sendSpecialMessage: (messageData) => {
      const specialMessage = {
        type: messageData.type,
        content: messageData.content,
        submissionId: messageData.submissionId,
        submissionData: messageData.submissionData,
        timestamp: Date.now(),
        id: `questionnaire_${Date.now()}`
      };
      setChatMessages(prev => [...prev, specialMessage]);
    },
    sendQuestionnaireMessage: async (message, submissionData) => {
      const userMessage = {
        type: 'user',
        content: message,
        timestamp: Date.now(),
        id: `user_${Date.now()}`
      };

      setChatMessages(prev => [...prev, userMessage, {
        type: 'questionnaire_loading',
        timestamp: Date.now() + 1,
        id: `questionnaire_loading_${Date.now()}`
      }]);

      try {
        const response = await sendMessageToDify(message, userId, conversationIdRef.current);

        if (response.conversation_id) {
          setConversationId(response.conversation_id);
          conversationIdRef.current = response.conversation_id;
        }

        const answer = response.answer || '抱歉，我现在无法回答这个问题。';

        setChatMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.type === 'questionnaire_loading') {
            return prev.slice(0, -1).concat([{
              type: 'thinking',
              timestamp: Date.now(),
              id: `thinking_${Date.now()}`
            }]);
          }
          return prev;
        });

        await handleAiAnswer(answer);

        setTimeout(() => {
          if (submissionData) {
            const successMessage = {
              type: 'questionnaire_success',
              content: `问卷提交成功！`,
              submissionId: submissionData.submissionId,
              submissionData: submissionData,
              timestamp: Date.now(),
              id: `questionnaire_success_${Date.now()}`
            };
            setChatMessages(prev => [...prev, successMessage]);
          }
        }, 500);

      } catch (error) {
        console.error('发送问卷消息失败:', error);
        setChatMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          const errorMessage = {
            type: 'ai',
            content: '抱歉，处理您的问卷时发生了错误，请稍后重试。',
            timestamp: Date.now(),
            id: `error_${Date.now()}`
          };

          if (lastMessage && lastMessage.type === 'questionnaire_loading') {
            return prev.slice(0, -1).concat([errorMessage]);
          } else {
            return [...prev, errorMessage];
          }
        });
      }
    }
  }));

  // 处理问卷详情弹窗 ESC 逻辑
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showQuestionnaireDetail) {
        closeQuestionnaireDetail();
      }
    };

    if (showQuestionnaireDetail) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showQuestionnaireDetail, closeQuestionnaireDetail]);

  const MessageContent = ({ message }) => {
    if (message.meta?.kind === 'initial_questionnaire_prompt') {
      return (
        <div className={styles.messageContent}>
          <div className={styles.initialQuestionnairePrompt}>
            <div className={styles.promptText}>{message.content}</div>
            <button
              className={`${styles.promptButton} ${initialQuestionnaireCompleted ? styles.completed : ''}`}
              onClick={handleOpenInitialQuestionnaire}
              title={initialQuestionnaireLocked ? '仅可查看' : (initialQuestionnaireCompleted ? '修改表单' : '')}
            >
              {initialQuestionnaireCompleted ? '已完成表单' : '打开问题表单'}
            </button>
          </div>
        </div>
      );
    }

    if (message.type === 'thinking') {
      return (
        <div className={styles.messageContent}>
          <LoadingAnimation
            message="AI正在思考中..."
            subMessage="正在为您分析和处理信息，请稍候"
            type="questionnaire"
          />
        </div>
      );
    }

    if (message.type === 'questionnaire_loading') {
      return (
        <div className={styles.messageContent}>
          <LoadingAnimation
            message="正在分析您的问卷..."
            subMessage="AI正在为您生成个性化的工艺推荐方案，请稍候"
            type="questionnaire"
          />
        </div>
      );
    }

    if (message.type === 'questionnaire_success') {
      if (!message.submissionData || !message.submissionData.results) {
        return (
          <div className={`${styles.messageContent} ${styles.questionnaireSuccess}`}>
            <div className={styles.successContent}>
              <h4>问卷提交成功！</h4>
              <p>数据加载中...</p>
            </div>
          </div>
        );
      }

      return (
        <div className={`${styles.messageContent} ${styles.questionnaireSuccess}`}>
          <div className={styles.successContent}>
            <h4>问卷提交成功！</h4>
            <p className={styles.successMessage}>
              您的问卷已成功提交，我们已为您生成了个性化的工艺推荐方案。
            </p>
            <button
              className={styles.viewDetailButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleViewQuestionnaireDetail(message.submissionData);
              }}
            >
              🔍 查看问卷详情
            </button>
          </div>
        </div>
      );
    }

    if (message.content.startsWith('提交问卷\n')) {
      return (
        <div className={`${styles.messageContent} ${styles.formSubmission}`}>
          问卷提交成功
        </div>
      );
    }

    const mdBlocks = message.type === 'ai' ? (message.mdBlocks || []) : [];
    const singleQuestionMeta = message.type === 'ai' ? (message.singleQuestion || null) : null;

    // 当“AI 返回文本有问题”时，之前逻辑会把 message.content 替换成 prefix，导致 mermaid token 不在 content 里，按钮被“忽略”。
    // 这里在渲染层做兜底：只要有 mermaidData，就强制追加一个 mermaid token，以确保按钮一定渲染出来。
    const MERMAID_TOKEN = '【mermaid流程图数据-点击复制-点击渲染】';
    const contentForRender = (() => {
      const base = String(message.content || '');
      if (!message.mermaidData) return base;
      if (base.includes(MERMAID_TOKEN)) return base;
      return `${base}${base ? '\n' : ''}${MERMAID_TOKEN}`;
    })();

    const shouldShowSingleQuestionButton = Boolean(
      singleQuestionMeta &&
      hasPendingSingleQuestion &&
      currentSingleQuestion?.id &&
      singleQuestionMeta?.id === currentSingleQuestion.id
    );

    // 渲染策略调整：
    // 不再使用互斥的 if...return，而是组合渲染。
    // 1. 渲染基础文本（包含 mermaid token）。
    // 2. 在文本下方追加 md 文件下载按钮（如果存在）。
    // 3. 在文本下方追加 single question 按钮（如果存在）。
    console.log(`[ChatDialog][MessageContent][render] msgId: ${message.id}, hasMdBlocks: ${mdBlocks.length > 0}, hasSingleQ: ${Boolean(singleQuestionMeta)}, shouldShowSingleQuestionButton: ${shouldShowSingleQuestionButton}`);

    const parts = String(contentForRender || '').split(MERMAID_TOKEN);
    console.log(`[ChatDialog][MessageContent][render] msgId: ${message.id}, mermaid parts count: ${parts.length}`);

    return (
      <div className={styles.messageContent}>
        {/* 1. 基础文本渲染，包含 mermaid 按钮 */}
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            <span dangerouslySetInnerHTML={{
              __html: String(part)
                .split('\n')
                .map(line => line.trim())
                .filter(line => line)
                .join('<br/>')
            }} />
            {i < parts.length - 1 && (
              <span
                className={styles.mermaidCopy}
                onClick={() => {
                  console.log('[ChatDialog][MessageContent] click mermaid token, hasMermaidData:', Boolean(message.mermaidData));
                  if (!message.mermaidData) {
                    alert('未获取到mermaid原始数据，无法复制/渲染');
                    return;
                  }
                  handleCopyAndRender(message.mermaidData);
                }}
              >
                {MERMAID_TOKEN}
              </span>
            )}
          </React.Fragment>
        ))}

        {/* 2. 文件下载按钮列表（如果存在） */}
        {mdBlocks.length > 0 && (
          <div className={styles.downloadList}>
            {mdBlocks.map((b, idx) => (
              <button
                key={`${b.fileName}_${idx}`}
                type="button"
                className={styles.downloadButton}
                onClick={() => downloadMarkdown(b.fileName, b.content)}
              >
                <span className={styles.downloadButtonText}>下载</span>
                <span className={styles.downloadSub} title={b.fileName}>{b.fileName}</span>
              </button>
            ))}
          </div>
        )}

        {/* 3. 单独问题按钮（如果存在） */}
        {shouldShowSingleQuestionButton && (
          <button
            type="button"
            className={styles.reopenQuestionButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSingleQuestionVisible(true);
            }}
          >
            打开问题表单
            <span className={styles.reopenQuestionSub}>{singleQuestionMeta?.prefix ? `对于${singleQuestionMeta.prefix}流程的问题` : '问题'}</span>
          </button>
        )}
      </div>
    );
  };

  if (!visible) return null;

  return (
    <div className={`${styles.dialogOverlay} ${isMobile ? styles.mobile : ''}`}>
      <div
        ref={dialogRef}
        className={`${styles.dialogContainer} ${isDragging ? styles.dragging : ''}`}
        style={position ? {
          left: `${position.x}px`,
          top: `${position.y}px`
        } : undefined}
      >
        <div
          className={styles.dialogHeader}
          onMouseDown={handleMouseDown}
        >
          <h2>北航器件制备工艺智能助手</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.messageContainer}>
          {chatMessages.map((message, index) => (
            <div
              key={message.id || message.timestamp || index}
              className={`${styles.messageWrapper} ${
                message.type === 'user' ? styles.userMessage :
                  message.type === 'questionnaire_success' ? styles.aiMessage :
                    styles.aiMessage
                }`}
            >
              <div className={styles.avatar}>
                <img
                  src={message.type === 'user' ? '/user.svg' : '/ai.svg'}
                  alt={message.type === 'user' ? 'User' : 'AI'}
                />
              </div>
              <MessageContent message={message} />
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            {questionnaireAttachmentVisible && (
              <div className={styles.attachmentBubble} title="问卷已作为本次提问附件发送">
                <span className={styles.attachmentText}>问卷</span>
              </div>
            )}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                flowEnded
                  ? "流程结束"
                  : isLoading
                    ? "AI正在思考中..."
                    : (!initialQuestionnaireCompleted ? "请先完成问卷..." : "请输入您的问题...")
              }
              className={`${styles.input} ${questionnaireAttachmentVisible ? styles.withAttachment : ''} ${flowEnded ? styles.disabledInput : ''}`}
              disabled={isLoading || flowEnded || !initialQuestionnaireCompleted}
            />
          </div>
          <button
            className={`${styles.sendButton} ${isLoading ? styles.loading : ''} ${flowEnded ? styles.disabledButton : ''}`}
            onClick={handleSend}
            disabled={isLoading || !initialQuestionnaireCompleted || flowEnded}
            title={flowEnded ? '流程结束' : (!initialQuestionnaireCompleted ? '请完成问题表单' : '')}
          >
            <img src="/send.svg" alt="发送" />
          </button>
        </div>
      </div>

      <SingleQuestion
        visible={singleQuestionVisible}
        question={singleQuestionWaiting ? null : currentSingleQuestion}
        history={singleQuestionHistory}
        isWaiting={singleQuestionWaiting}
        isFlowEnded={flowEnded}
        onClose={() => setSingleQuestionVisible(false)}
        onSelect={async (selectedOption) => {
          // 将本次问题和答案写入历史记录（只读展示），并切换当前区域为等待态
          const q = currentSingleQuestion;
          if (q) {
            setSingleQuestionHistory((prev) => {
              const picked = (q.options || []).find((o) => o.letter === selectedOption);
              const record = {
                historyId: `sq_${Date.now()}`,
                id: q.id,
                title: q.title,
                prefix: q.prefix,
                selectedOption: selectedOption,
                selectedOptionText: picked?.text || ''
              };
              return [...prev, record];
            });
          }

          setHasPendingSingleQuestion(false);
          setCurrentSingleQuestion(null);
          setSingleQuestionWaiting(true);

          await sendAutoQuery(selectedOption, { reason: 'single_question_select', showUserBubble: true });
        }}
      />

      <InitialQuestionnaire
        visible={showInitialQuestionnaire}
        mode={initialQuestionnaireLocked ? 'view' : (initialQuestionnaireCompleted ? 'edit' : 'create')}
        initialAnswers={initialQuestionnaireAnswers}
        initialView={initialQuestionnaireLocked ? 'overview' : (initialQuestionnaireCompleted ? 'overview' : 'form')}
        onClose={() => setShowInitialQuestionnaire(false)}
        onComplete={handleInitialQuestionnaireComplete}
        deviceName={initialDeviceName}
        onDeviceNameChange={setInitialDeviceName}
      />

      {showQuestionnaireDetail && currentQuestionnaireDetail && (
        <div className={styles.detailOverlay} onClick={closeQuestionnaireDetail}>
          <div className={styles.detailContainer} onClick={e => e.stopPropagation()}>
            <div className={styles.detailHeader}>
              <h3>问卷详情</h3>
              <button
                className={styles.closeButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeQuestionnaireDetail();
                }}
                title="关闭 (ESC)"
              >
                ×
              </button>
            </div>

            <div className={styles.detailContent}>
              <div className={styles.detailSection}>
                <h4>📋 基本信息</h4>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>提交时间</span>
                    <span className={styles.infoValue}>
                      {new Date(currentQuestionnaireDetail.submitTime).toLocaleString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>涉及工艺</span>
                    <span className={styles.infoValue}>
                      {currentQuestionnaireDetail.processes.map((process, index) => (
                        <span key={process} className={styles.processTag}>
                          {process}
                          {index < currentQuestionnaireDetail.processes.length - 1 && '、'}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4>🔍 答题路径</h4>
                {Object.entries(currentQuestionnaireDetail.answers).map(([process, answers]) => (
                  <div key={process} className={styles.processAnswers}>
                    <div className={styles.processHeader}>
                      <span className={styles.processIcon}>⚙️</span>
                      <h5>{process}</h5>
                      <span className={styles.questionCount}>({answers.length} 个问题)</span>
                    </div>
                    <div className={styles.answersList}>
                      {answers.map((answer, index) => (
                        <div key={index} className={styles.answerItem}>
                          <div className={styles.questionNumber}>Q{index + 1}</div>
                          <div className={styles.questionContent}>
                            <div className={styles.question}>{answer.question}</div>
                            <div className={styles.answer}>
                              <span className={styles.answerLabel}>您的选择：</span>
                              <span className={styles.answerValue}>{answer.selectedOption}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.detailSection}>
                <h4>🎯 推荐结果</h4>
                <div className={styles.finalResults}>
                  {Object.entries(currentQuestionnaireDetail.results).map(([process, result]) => (
                    <div key={process} className={styles.finalResultItem}>
                      <div className={styles.resultHeader}>
                        <span className={styles.resultIcon}>✅</span>
                        <span className={styles.resultProcess}>{process}</span>
                      </div>
                      <div className={styles.resultValue}>{result}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatDialog;
